import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { GOVERNANCE, INTEGRATIONS, slugify } from "./constants";

export function LandingCompliance() {
  return (
    <section id="compliance" className="py-20 border-b border-zinc-200 bg-zinc-950 text-zinc-100">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300/80">Compliance &amp; Governance</div>
            <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl mt-3">Built with Governance in Mind</h2>
            <p className="mt-4 text-zinc-300 max-w-xl">
              GuestIntel is engineered from the ground up to meet the highest standards of fairness, security and regulatory readiness — including GDPR-aligned data subject rights.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/trust"><Button variant="outline" className="rounded-full border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white" data-testid="compliance-trust-btn">Visit the Trust Center <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/appeal"><Button variant="ghost" className="rounded-full text-zinc-100 hover:text-white hover:bg-zinc-900" data-testid="compliance-appeal-btn">Guest appeal portal</Button></Link>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GOVERNANCE.map((g) => (
              <li key={g} className="flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-md px-4 py-3" data-testid={`governance-${slugify(g)}`}>
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-sm">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function LandingPricing() {
  // Pricing replaced with a "Talk to sales" CTA. GuestIntel sells offline:
  // every deal is bespoke, signed under MSA, and onboarded by the founding-
  // member program below. Showing tier prices on the website created the
  // wrong expectation that prospects could self-serve checkout.
  return (
    <section id="pricing" className="py-20 border-b border-zinc-200 bg-stone-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Pricing</div>
        <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl mt-3">
          Built for your portfolio
        </h2>
        <p className="mt-4 text-zinc-700 max-w-2xl mx-auto">
          GuestIntel is sold under a Master Services Agreement, scoped to the size of your group,
          the regions you operate in, and the compliance regime you answer to. There's no public
          price list — let's talk and design the right footprint together.
        </p>
        <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
          {[
            { title: "Single property", body: "Independent hotels, resorts, serviced apartments." },
            { title: "Multi-property group", body: "Regional or international portfolios with a central admin." },
            { title: "Enterprise / OTA", body: "Custom verticals, audit reporting, dedicated CSM." },
          ].map((card) => (
            <div
              key={card.title}
              className="border border-zinc-200 rounded-md p-5 bg-white"
              data-testid={`pricing-card-${slugify(card.title)}`}
            >
              <div className="font-display font-bold">{card.title}</div>
              <p className="mt-2 text-sm text-zinc-600">{card.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/contact">
            <Button className="rounded-full" data-testid="pricing-contact-btn">
              Talk to sales <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <a href={`${process.env.REACT_APP_APP_URL || ""}/register`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-full" data-testid="pricing-register-btn">
              Start a free assessment
            </Button>
          </a>
        </div>
        <p className="mt-6 text-xs text-zinc-500">
          Already a member? <a href={`${process.env.REACT_APP_APP_URL || ""}/login`} target="_blank" rel="noopener noreferrer" className="underline">Sign in</a>
        </p>
      </div>
    </section>
  );
}

export function LandingIntegrations() {
  return (
    <section id="integrations" className="py-20 border-b border-zinc-200 bg-stone-50">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Integrations</div>
        <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl mt-3">Works With</h2>
        <p className="mt-4 text-zinc-700 max-w-2xl mx-auto">Drop GuestIntel into the systems your team already runs.</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {INTEGRATIONS.map((i) => (
            <div
              key={i}
              className="border border-zinc-200 rounded-full px-5 py-2 text-sm bg-white"
              data-testid={`integration-${slugify(i)}`}
            >
              {i}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
